import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { AnswerTypeNames } from "models/AnswerType";
import Question from "models/Question";
import React, { useState, useEffect, useCallback } from "react";
import { ModuleService } from "services/ModuleService";

type DataLabelingModuleQuestionSelectorProps = {
    moduleId?: string;
} & Partial<Omit<React.ComponentPropsWithoutRef<typeof AcxSelectSingle<Question>>, "options">>;

function shouldShowQuestion(question: Question) {
    const excludeAnswerTypes: string[] = [
        AnswerTypeNames.QuestionGrouping,
        AnswerTypeNames.StarRating,
        AnswerTypeNames.TextResponse,
        AnswerTypeNames.DateResponse,
    ];

    return !excludeAnswerTypes.includes(question.answerType.answerTypeName);
}

export function DataLabelingModuleQuestionSelector(
    props: DataLabelingModuleQuestionSelectorProps,
) {
    const [options, setOptions] = useState<Question[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setOptions([]);
    }, [props.moduleId]);

    const fetchOptions = useCallback(async () => {
        if (isLoading || options.length > 0 || !props.moduleId) return;

        try {
            setIsLoading(true);
            const service = new ModuleService();
            const module = await service.getLicensedModule(props.moduleId);
            const allQuestions = module.questions
                .filter((question) => question.isActive)
                .flatMap<Question>((question) => [
                    question,
                    ...(Object.hasOwn(question, "questions")
                        ? question["questions"]
                        : []),
                ])
                .map((question) => ({
                    ...question,
                    tags: question.tags.filter((tag) => tag.isActive),
                }));
            setOptions(allQuestions.filter(shouldShowQuestion));
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, options, props.moduleId]);

    return (
        <AcxSelectSingle
            id="question"
            valueField="questionText"
            labelField="questionText"
            fullWidth
            {...props}
            isLoading={isLoading}
            options={options}
            isDisabled={!props.moduleId || props.isDisabled}
            onMenuOpen={fetchOptions}
        />
    );
}
